// AGC: Tournament Payment Hook
// AGC: Handles OGB approval and tournament entry fee payment
import { useState, useCallback } from 'react'
import { ethers } from 'ethers'
import { useAccount } from 'wagmi'
import { getSafeSigner } from '../utils/getBrowserProvider'
import { useFeatureFlags } from './useFeatureFlags'

const ENTRY_FEE = ethers.parseEther('50') // 50 OGB

export const useTournamentPayment = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { address, connector } = useAccount()
  const { isFarcasterContext } = useFeatureFlags()

  const normalizeTournamentId = (rawId: string): `0x${string}` => {
    if (!rawId) {
      throw new Error('Tournament ID is required')
    }

    if (ethers.isHexString(rawId, 32)) {
      return rawId as `0x${string}`
    }

    return ethers.id(rawId)
  }

  const payEntry = useCallback(async (tournamentId: string): Promise<string | null> => {
    if (!address) {
      setError('Wallet not connected')
      return null
    }

    setIsLoading(true)
    setError(null)

    try {
      console.log('🏆 TournamentLobby: Starting payment flow...')
      console.log(`   Tournament ID: ${tournamentId}`)
      console.log(`   Player: ${address}`)

      // AGC: Get signer with timeout to prevent hanging
      console.log('   Getting signer from connector...')
      if (!connector) {
        throw new Error('Wallet connector not available. Please connect your wallet.')
      }

      const signerPromise = getSafeSigner({
        connector,
        isFarcasterContext,
        expectedAddress: address as `0x${string}`
      })

      // AGC: Add timeout to prevent infinite hanging (30 seconds)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Wallet connection timeout. Please try again.')), 30000)
      })

      const signer = await Promise.race([signerPromise, timeoutPromise]) as Awaited<ReturnType<typeof getSafeSigner>>

      // AGC: Get contract addresses
      const OGB_TOKEN_ADDRESS = import.meta.env.VITE_OGB_TOKEN_ADDRESS
      const TOURNAMENT_ADDRESS = import.meta.env.VITE_ROUNDROBIN_TOURNAMENT_ADDRESS

      if (!OGB_TOKEN_ADDRESS) {
        throw new Error('OGB token address not configured')
      }

      if (!TOURNAMENT_ADDRESS) {
        throw new Error('Tournament contract address not configured')
      }

      console.log(`   OGB Token: ${OGB_TOKEN_ADDRESS}`)
      console.log(`   Tournament Contract: ${TOURNAMENT_ADDRESS}`)

      // AGC: Create contract instances
      const ogbABI = [
        'function balanceOf(address account) external view returns (uint256)',
        'function approve(address spender, uint256 amount) external returns (bool)',
        'function allowance(address owner, address spender) external view returns (uint256)'
      ]

      const ogbToken = new ethers.Contract(OGB_TOKEN_ADDRESS, ogbABI, signer)

      // AGC: 1. Check balance
      const balance = await ogbToken.balanceOf(address)
      console.log(`   Current OGB balance: ${ethers.formatEther(balance)} OGB`)

      if (balance < ENTRY_FEE) {
        throw new Error(`Insufficient OGB balance. Need 50 OGB, have ${ethers.formatEther(balance)} OGB`)
      }

      // AGC: 2. Check allowance
      const currentAllowance = await ogbToken.allowance(address, TOURNAMENT_ADDRESS)
      console.log(`   Current allowance: ${ethers.formatEther(currentAllowance)} OGB`)

      if (currentAllowance < ENTRY_FEE) {
        // AGC: SECURITY FIX (2025-11-27) - Approve exact amount instead of unlimited
        // AGC: No unlimited authorizations - only approve what's needed for this tournament entry
        console.log('   Approving OGB token (exact amount)...')
        const approveTx = await ogbToken.approve(TOURNAMENT_ADDRESS, ENTRY_FEE)
        console.log(`   Approval TX: ${approveTx.hash}`)

        let approveReceipt
        try {
          approveReceipt = await Promise.race([
            approveTx.wait(),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Transaction confirmation timeout')), 120000)
            )
          ])
          console.log(`   ✅ Approval confirmed (block ${approveReceipt.blockNumber})`)
        } catch (waitError: any) {
          if (waitError.message?.includes('timeout')) {
            console.warn(`  ⚠️  Approval timeout (TX sent: ${approveTx.hash}) - continuing...`)
            // AGC: Create a mock receipt for timeout case
            approveReceipt = { blockNumber: 0 }
          } else {
            throw waitError
          }
        }

        // AGC: Verify new allowance (with timeout to prevent infinite loop)
        console.log('   Verifying approval on-chain...')
        let newAllowance = await ogbToken.allowance(address, TOURNAMENT_ADDRESS)
        let attempts = 0
        const maxAttempts = 10
        const delayMs = 2000 // AGC: Increased delay to 2 seconds for RPC propagation

        while (newAllowance < ENTRY_FEE && attempts < maxAttempts) {
          attempts++
          console.log(`   Allowance check attempt ${attempts}/${maxAttempts}...`)
          await new Promise(resolve => setTimeout(resolve, delayMs))

          try {
            newAllowance = await ogbToken.allowance(address, TOURNAMENT_ADDRESS)
            console.log(`   Current allowance: ${ethers.formatEther(newAllowance)} OGB`)
          } catch (allowanceError: any) {
            console.warn(`   Allowance check error (attempt ${attempts}):`, allowanceError.message)
            // AGC: Continue retrying unless we've exhausted attempts
            if (attempts >= maxAttempts) {
              throw new Error(`Approval verification failed after ${maxAttempts} attempts: ${allowanceError.message}`)
            }
          }
        }

        if (newAllowance < ENTRY_FEE) {
          throw new Error(`Approval verification failed. Allowance still insufficient after ${maxAttempts} attempts.`)
        }

        console.log(`   ✅ New allowance: ${ethers.formatEther(newAllowance)} OGB`)
      } else {
        console.log('   ✅ Already approved')
      }

      // AGC: 3. Pay entry fee
      console.log('   Paying 50 OGB entry fee...')
      const tournamentABI = [
        'function payEntry(bytes32 tournamentId) external'
      ]
      const tournamentContract = new ethers.Contract(TOURNAMENT_ADDRESS, tournamentABI, signer)

      // AGC: Convert tournamentId string to bytes32 (no double-hash)
      const tournamentIdBytes32 = normalizeTournamentId(tournamentId)
      console.log(`   Tournament ID bytes32: ${tournamentIdBytes32}`)
      console.log(`   Contract address: ${TOURNAMENT_ADDRESS}`)
      
      // AGC: Keep the payment call lightweight so wallet prompts appear instantly
      const payTx = await tournamentContract.payEntry(tournamentIdBytes32)
      console.log(`   Payment TX: ${payTx.hash}`)
      
      let receipt
      try {
        receipt = await Promise.race([
          payTx.wait(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Transaction confirmation timeout')), 120000)
          )
        ])
        console.log(`   ✅ Payment confirmed (block ${receipt.blockNumber})`)
      } catch (waitError: any) {
        if (waitError.message?.includes('timeout')) {
          console.warn(`  ⚠️  Payment timeout (TX sent: ${payTx.hash}) - continuing...`)
          // AGC: Return the transaction hash even if confirmation timed out
          return payTx.hash
        } else {
          throw waitError
        }
      }

      return receipt.hash
    } catch (err: any) {
      const errorMessage = err.message || 'Payment failed'
      console.error('❌ TournamentLobby: Payment error:', errorMessage)
      setError(errorMessage)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [address, connector, isFarcasterContext])

  return { payEntry, isLoading, error }
}
